import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDownloads } from '../context/DownloadsContext';
import API_URL from '../config/api';
import useSEO from '../hooks/useSEO';
import GameProperties from '../components/GameProperties';
import SimilarGames from '../components/SimilarGames';
import GamePrice from '../components/GamePrice';
import AddCardModal from '../components/AddCardModal';
import { usesUsdCheckout, isFreeGame } from '../utils/gamePrice';
import { getStripe } from '../config/stripe';

function GameDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const { addDownload, updateDownloadProgress, completeDownload, failDownload } = useDownloads();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isPurchased, setIsPurchased] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [currentScreenshotIndex, setCurrentScreenshotIndex] = useState(0);
  const [downloading, setDownloading] = useState({ platform: null, type: null });
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [openConfirmAfterCard, setOpenConfirmAfterCard] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const imageRef = useRef(null);
  const screenshotsRef = useRef(null);

  const fetchPaymentMethods = useCallback(async () => {
    if (!token) {
      setHasPaymentMethod(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/stripe/payment-methods`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setHasPaymentMethod(Boolean(data.hasPaymentMethod));
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchPaymentMethods();
    } else {
      setHasPaymentMethod(false);
    }
  }, [isAuthenticated, token, fetchPaymentMethods]);

  const checkOwnership = useCallback(async () => {
    if (!isAuthenticated || !token) return;

    try {
      const response = await fetch(`${API_URL}/purchases/games`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const ownedGameIds = data.games.map(g => g.gameId);
        if (ownedGameIds.includes(parseInt(id))) {
          setIsPurchased(true);
        }
      }
    } catch (err) {
      console.error('Error checking ownership:', err);
    }
  }, [isAuthenticated, token, id]);

  const fetchGame = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/games/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch game');
      }
      const data = await response.json();
      setGame(data);
      setSelectedImage(data.mainImage || data.featuredImage);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching game:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load liked and wishlist state from API (only when loaded game matches route id)
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated || !token) {
      setIsLiked(false);
      setIsInWishlist(false);
      return;
    }

    const routeId = Number(id);
    const loadedId = Number(game?.id);
    if (
      !Number.isInteger(routeId) ||
      routeId < 1 ||
      !Number.isInteger(loadedId) ||
      loadedId !== routeId
    ) {
      setIsLiked(false);
      setIsInWishlist(false);
      return;
    }

    const loadLibraryState = async () => {
      try {
        const [likedResponse, wishlistResponse] = await Promise.all([
          fetch(`${API_URL}/library/liked`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/library/wishlist`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        const gid = loadedId;

        if (likedResponse.ok) {
          const likedData = await likedResponse.json();
          const ids = likedData.gameIds || [];
          setIsLiked(ids.some((x) => Number(x) === gid));
        }

        if (wishlistResponse.ok) {
          const wishlistData = await wishlistResponse.json();
          const ids = wishlistData.gameIds || [];
          setIsInWishlist(ids.some((x) => Number(x) === gid));
        }
      } catch (error) {
        console.error('Error loading library state:', error);
      }
    };

    loadLibraryState();
  }, [id, game?.id, isAuthenticated, token, authLoading]);

  useEffect(() => {
    // Scroll to top immediately for mobile compatibility
    window.scrollTo(0, 0);
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
    
    fetchGame();
    checkOwnership();
    
    // Ensure scroll happens after content loads
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
    }, 100);
  }, [id, isAuthenticated, fetchGame, checkOwnership]);

  // SEO updates based on game data
  useSEO({
    title: game ? `${game.title} by ${game.developer} | Nightfall` : 'Game Details | Nightfall',
    description: game ? game.description.substring(0, 160) : 'View game details on Nightfall',
    image: game ? (game.featuredImage || game.mainImage) : null,
    url: game ? `https://nightfallshop.com/game/${id}` : null,
    type: 'website'
  });

  // Add structured data for game
  useEffect(() => {
    if (!game) return;

    // Remove existing game structured data
    const existingScript = document.getElementById('game-structured-data');
    if (existingScript) {
      existingScript.remove();
    }

    // Create new structured data
    const script = document.createElement('script');
    script.id = 'game-structured-data';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'VideoGame',
      name: game.title,
      description: game.description,
      image: game.featuredImage || game.mainImage,
      gamePlatform: ['Windows', 'Mac', 'Android'],
      applicationCategory: 'Game',
      offers: {
        '@type': 'Offer',
        price:
          typeof game.costUSD === 'number'
            ? game.costUSD.toFixed(2)
            : game.shards === 0
              ? '0'
              : game.shards.toString(),
        priceCurrency: typeof game.costUSD === 'number' ? 'USD' : 'Shards',
        availability: 'https://schema.org/InStock',
      },
      author: {
        '@type': 'Organization',
        name: game.developer
      },
      publisher: {
        '@type': 'Organization',
        name: game.creator?.name || game.developer
      }
    });
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('game-structured-data');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [game, id]);

  const openScreenshotModal = (index) => {
    setCurrentScreenshotIndex(index);
    setShowScreenshotModal(true);
  };

  const navigateScreenshot = useCallback((direction) => {
    if (!game?.screenshots) return;
    const newIndex = currentScreenshotIndex + direction;
    if (newIndex >= 0 && newIndex < game.screenshots.length) {
      setCurrentScreenshotIndex(newIndex);
    } else if (newIndex < 0) {
      setCurrentScreenshotIndex(game.screenshots.length - 1);
    } else {
      setCurrentScreenshotIndex(0);
    }
  }, [game?.screenshots, currentScreenshotIndex]);

  const closeScreenshotModal = () => {
    setShowScreenshotModal(false);
  };

  // Keyboard navigation for screenshot modal
  useEffect(() => {
    if (!showScreenshotModal || !game?.screenshots) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeScreenshotModal();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateScreenshot(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateScreenshot(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showScreenshotModal, currentScreenshotIndex, game?.screenshots, navigateScreenshot]);

  // Match screenshots container height to image height
  useEffect(() => {
    if (!imageRef.current || !screenshotsRef.current) return;
    
    const updateHeight = () => {
      if (imageRef.current && screenshotsRef.current) {
        const imageHeight = imageRef.current.offsetHeight;
        screenshotsRef.current.style.height = `${imageHeight}px`;
      }
    };
    
    updateHeight();
    
    // Update on window resize
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [game, selectedImage]);

  const handleDownload = async (platform, type = 'full') => {
    console.warn('[Download] handleDownload called (check Browser DevTools > Console)', { platform, type, gameId: id });
    if (!isAuthenticated || !token) {
      console.log('[Download] Not authenticated, redirecting to signin');
      navigate('/signin');
      return;
    }

    setDownloading({ platform, type });

    try {
      console.log('[Download] Fetching URL from backend', `${API_URL}/downloads/game/${id}/${platform}/${type}`);
      const response = await fetch(`${API_URL}/downloads/game/${id}/${platform}/${type}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.url) {
        
        // Create unique download ID
        const downloadId = `${id}_${platform}_${type}_${Date.now()}`;
        const fileName = data.fileName || 'download';
        const fileSize = data.fileSize || 0; // File size in bytes from backend
        
        // Create abort controller for cancellation
        const abortController = new AbortController();
        
        // Store reader reference for cancellation
        let readerRef = null;
        let isCancelled = false;
        
        const cancelHandler = () => {
          isCancelled = true;
          if (readerRef) readerRef.cancel().catch(() => {});
          setDownloading({ platform: null, type: null });
        };
        
        // Add download to context with abort controller and cancel handler
        addDownload(downloadId, fileName, game?.title || 'Game', platform, type, abortController, cancelHandler);
        
        // Use direct (signed) URL for large files to avoid ERR_HTTP2_PROTOCOL_ERROR through nginx
        const LARGE_FILE_THRESHOLD = 20 * 1024 * 1024; // 20 MB
        const useDirect = (data.fileSize || 0) > LARGE_FILE_THRESHOLD;
        const proxyUrl = data.proxyUrl ? `${API_URL}${data.proxyUrl}` : null;
        const downloadUrl = useDirect ? data.url : (proxyUrl || data.url);
        if (!downloadUrl.includes('/proxy/')) {
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          completeDownload(downloadId);
          setDownloading({ platform: null, type: null });
          return;
        }
        // Fetch with auth token and create blob download with progress tracking
        fetch(downloadUrl, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            signal: abortController.signal
          })
          .then(response => {
            console.log('[Download] Proxy fetch response:', response.status, response.ok, 'content-length:', response.headers.get('content-length'));
            if (!response.ok) throw new Error('Download failed');
            
            const contentLength = response.headers.get('content-length');
            const total = contentLength ? parseInt(contentLength, 10) : fileSize;
            
            if (!response.body) {
              throw new Error('Response body is null');
            }
            
            const reader = response.body.getReader();
            readerRef = reader; // Store reference for cancellation
            const chunks = [];
            let receivedLength = 0;
            
            function pump() {
              // Check if cancelled before reading
              if (isCancelled || abortController.signal.aborted) {
                reader.cancel().catch(() => {});
                return Promise.resolve();
              }
              return reader.read().then(({ done, value }) => {
                if (isCancelled || abortController.signal.aborted) {
                  reader.cancel().catch(() => {});
                  return Promise.resolve();
                }
                if (done) {
                  if (!isCancelled) {
                    updateDownloadProgress(downloadId, 100);
                    const blob = new Blob(chunks);
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    completeDownload(downloadId);
                    setDownloading({ platform: null, type: null });
                  }
                  return;
                }
                
                chunks.push(value);
                receivedLength += value.length;
                
                // If we received all bytes (or 99.9%+, in case Content-Length is wrong or stream never signals done), complete
                const receivedAll = total > 0 && receivedLength >= total;
                const receivedNearlyAll = total > 0 && receivedLength >= Math.floor(total * 0.999);
                if ((receivedAll || receivedNearlyAll) && !isCancelled) {
                  updateDownloadProgress(downloadId, 100);
                  const blob = new Blob(chunks);
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  completeDownload(downloadId);
                  setDownloading({ platform: null, type: null });
                  return Promise.resolve();
                }
                
                const pct = total > 0 ? (receivedLength / total) * 100 : 0;
                updateDownloadProgress(downloadId, pct >= 99 ? 100 : Math.min(100, pct));
                return pump();
              }).catch(error => {
                if (error.name === 'AbortError' || isCancelled) return Promise.resolve();
                throw error;
              });
            }
            
            return pump();
          })
          .catch(error => {
            if (readerRef) readerRef.cancel().catch(() => {});
            if (error.name === 'AbortError' || isCancelled) return;
            console.error('[Download] Proxy fetch failed:', error.message, error);
            failDownload(downloadId, error.message || 'Download failed');
            setDownloading({ platform: null, type: null });
          });
      } else {
        console.error('[Download] Backend error:', data.error || data.details);
        setDownloading({ platform: null, type: null });
        alert(data.error || data.details || 'Download failed. Please try again.');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Download error:', error);
      setDownloading({ platform: null, type: null });
      alert('Network error. Please try again.');
    }
  };

  const completeUsdPurchase = async (paymentIntentId) => {
    const response = await fetch(`${API_URL}/stripe/complete-game-purchase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ paymentIntentId }),
    });
    return response.json();
  };

  const handlePurchase = async () => {
    if (!isAuthenticated || !token) {
      setPurchaseError('Please sign in to purchase games');
      setShowConfirmModal(false);
      navigate('/signin');
      return;
    }

    if (usesUsdCheckout(game)) {
      if (!hasPaymentMethod) {
        setShowConfirmModal(false);
        setOpenConfirmAfterCard(true);
        setShowAddCardModal(true);
        return;
      }

      setPurchasing(true);
      setPurchaseError('');

      try {
        const response = await fetch(`${API_URL}/stripe/purchase-game`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ gameId: parseInt(id, 10) }),
        });

        const data = await response.json();

        if (data.requiresAction && data.clientSecret) {
          const stripe = await getStripe(token);
          if (!stripe) {
            setPurchaseError('Payments are unavailable right now. Please try again later.');
            return;
          }

          const { error, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret);

          if (error) {
            setPurchaseError(error.message || 'Payment authentication failed');
            return;
          }

          if (paymentIntent?.status === 'succeeded') {
            await completeUsdPurchase(paymentIntent.id);
            setIsPurchased(true);
            setShowConfirmModal(false);
            setShowSuccessModal(true);
            await checkOwnership();
          }
          return;
        }

        if (response.ok && data.success) {
          setIsPurchased(true);
          setShowConfirmModal(false);
          setShowSuccessModal(true);
          await checkOwnership();
        } else if (response.status === 409) {
          setIsPurchased(true);
          setShowConfirmModal(false);
          await checkOwnership();
        } else if (data.code === 'NO_PAYMENT_METHOD') {
          setShowConfirmModal(false);
          setOpenConfirmAfterCard(true);
          setShowAddCardModal(true);
        } else {
          setPurchaseError(data.error || 'Purchase failed');
        }
      } catch (error) {
        console.error('Purchase error:', error);
        setPurchaseError('Network error. Please try again.');
      } finally {
        setPurchasing(false);
      }
      return;
    }

    setPurchaseError('This game is not available for card checkout yet.');
  };

  if (loading) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
        <div className="text-white text-center py-20">Loading game...</div>
      </main>
    );
  }

  if (error || !game) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
        <div className="text-white text-center py-20">Game not found</div>
      </main>
    );
  }

  // Wishlist and Like handlers
  const handleWishlistToggle = async () => {
    if (!isAuthenticated || !token) {
      navigate('/signin');
      return;
    }
    
    const newState = !isInWishlist;
    setIsInWishlist(newState);
    
    const gid = Number(game.id);
    try {
      let res;
      if (newState) {
        res = await fetch(`${API_URL}/library/wishlist`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ gameId: gid })
        });
      } else {
        res = await fetch(`${API_URL}/library/wishlist/${gid}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      if (!res.ok) {
        setIsInWishlist(!newState);
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      setIsInWishlist(!newState);
    }
  };

  const handleLikeToggle = async () => {
    if (!isAuthenticated || !token) {
      navigate('/signin');
      return;
    }
    
    const newState = !isLiked;
    setIsLiked(newState);
    
    const gid = Number(game.id);
    try {
      let res;
      if (newState) {
        res = await fetch(`${API_URL}/library/liked`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ gameId: gid })
        });
      } else {
        res = await fetch(`${API_URL}/library/liked/${gid}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      if (!res.ok) {
        setIsLiked(!newState);
      }
    } catch (error) {
      console.error('Error updating liked games:', error);
      setIsLiked(!newState);
    }
  };

  return (
    <>
    <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
      <style>{`
        .game-detail-align {
          margin-top: calc(5.75rem + 0.5rem - 6rem);
        }
        @media (min-width: 768px) {
          .game-detail-align {
            margin-top: calc(5.75rem + 1.25rem - 8rem);
          }
        }
        .screenshots-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .screenshots-scroll::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
          margin: 4px 0;
        }
        .screenshots-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 10px;
          border: 2px solid transparent;
          background-clip: padding-box;
          transition: background 0.2s ease;
        }
        .screenshots-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.25);
          background-clip: padding-box;
        }
        .screenshots-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
        }
        @media (min-width: 1024px) {
          .game-detail-align > div {
            align-items: stretch;
          }
          .image-container {
            align-self: stretch;
          }
          .screenshots-container {
            align-self: stretch;
            display: flex;
            flex-direction: column;
            height: 100%;
          }
          .screenshots-container .screenshots-scroll {
            flex: 1 1 0;
            min-height: 0;
            overflow-y: auto;
            overscroll-behavior: contain;
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>

      <div className="w-full">
        {/* Breadcrumb Navigation */}
        <div className="game-detail-align mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/')}
              className="text-white/60 hover:text-white transition-colors duration-200 text-sm font-medium"
            >
              Store
            </button>
            <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {game && (
              <span className="text-white/60 text-sm font-medium">{game.title}</span>
            )}
          </div>
        </div>

        {/* Main Grid: Left Column (2/3) + Right Column (1/3) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 pb-8">
          {/* Left Column: Image + Title Card + About + Tags + Properties + Similar Games */}
          <div className="lg:col-span-2 flex flex-col gap-6 lg:gap-8">
            {/* Cover Image */}
            <div ref={imageRef} className="w-full relative overflow-hidden rounded-3xl border border-dark-border shadow-xl" style={{ aspectRatio: '16/9' }}>
              {selectedImage ? (
                <img 
                  src={selectedImage} 
                  alt={game.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-dark-border via-dark-card to-dark-surface flex items-center justify-center">
                  <div className="text-white/40">Game Image</div>
                </div>
              )}
            </div>

            {/* Title Card - Directly below image */}
            <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <h1 className="font-semibold text-3xl lg:text-4xl text-white mb-3">{game.title}</h1>
                  <p className="text-base lg:text-lg text-white/60">
                    by{' '}
                    {game.creator?.id ? (
                      <button
                        onClick={() => {
                          window.scrollTo(0, 0);
                          navigate(`/creator/${game.creator.id}`);
                        }}
                        className="hover:text-white transition-colors duration-200 underline decoration-white/40 hover:decoration-white/60"
                      >
                        {game.creator.name || game.developer}
                      </button>
                    ) : (
                      game.developer
                    )}
                  </p>
                </div>
                {/* Wishlist and Like Buttons */}
                <div className="flex gap-2 flex-shrink-0">
                  <button 
                    onClick={handleWishlistToggle}
                    className={`py-2.5 px-4 border rounded-xl text-white font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                      isInWishlist 
                        ? 'bg-accent-blue/20 border-accent-blue/50 hover:bg-accent-blue/30' 
                        : 'bg-dark-bg/50 border-dark-border hover:bg-dark-bg/70 hover:border-accent-blue/30'
                    }`}
                  >
                    {isInWishlist ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    )}
                    {isInWishlist ? 'In Wishlist' : 'Wishlist'}
                  </button>
                  <button 
                    onClick={handleLikeToggle}
                    className={`w-10 h-10 border rounded-xl transition-all duration-200 flex items-center justify-center ${
                      isLiked 
                        ? 'bg-red-500/20 border-red-500/50 text-red-400 hover:bg-red-500/30' 
                        : 'bg-dark-bg/50 border-dark-border text-white hover:bg-dark-bg/70 hover:border-accent-blue/30'
                    }`}
                  >
                    <svg className="w-5 h-5" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* About Section */}
            {game.description && (
              <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8">
                <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4 lg:mb-6">About</h2>
                <p className="text-base lg:text-lg text-white/80 leading-relaxed whitespace-pre-line">{game.description}</p>
              </div>
            )}

            {/* Tags Section */}
            {game.tags && game.tags.length > 0 && (
              <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8">
                <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4 lg:mb-6">Tags</h2>
                <div className="flex flex-wrap gap-3">
                  {game.tags.map(tag => (
                    <span 
                      key={tag.id}
                      className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105"
                      style={{ 
                        backgroundColor: `${tag.color}15`, 
                        border: `1px solid ${tag.color}40`, 
                        color: tag.color 
                      }}
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Game Properties */}
            {game.properties && (
              <div>
                <GameProperties properties={game.properties} />
              </div>
            )}

            {/* Similar Games */}
            <div>
              <SimilarGames currentGame={game} />
            </div>
          </div>

          {/* Right Column: Screenshots + Purchase Card */}
          <div className="lg:col-span-1 flex flex-col gap-6">
              {game.screenshots && game.screenshots.length > 0 && (
                <div ref={screenshotsRef} className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-4 lg:p-6 flex flex-col" style={{ minHeight: 0 }}>
                  <h3 className="text-lg lg:text-xl font-semibold text-white mb-4 flex-shrink-0">Screenshots</h3>
                  <div className="flex-1 overflow-y-auto screenshots-scroll" style={{ minHeight: 0 }}>
                    <div className="flex flex-col gap-3">
                      {game.screenshots.map((screenshot, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            openScreenshotModal(index);
                          }}
                          className="w-full aspect-[16/9] rounded-xl overflow-hidden border-2 border-dark-border flex-shrink-0"
                        >
                          <img 
                            src={screenshot} 
                            alt={`${game.title} screenshot ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Purchase Card - Under Screenshots */}
                <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8 sticky top-5">
                  <div className="mb-6 pb-6 border-b border-dark-border/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white/60 text-sm uppercase tracking-wider mb-2">Price</div>
                        <div className="text-2xl lg:text-3xl">
                          <GamePrice game={game} isOwned={isPurchased} size="lg" />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Demo Download Section */}
                  {game.demoVersion && !isPurchased && game.demoDownloads && (game.demoDownloads.windows || game.demoDownloads.mac || game.demoDownloads.android) && (
                    <div className="mb-6 pb-6 border-b border-dark-border/50">
                      <div className="mb-4">
                        <h3 className="text-base md:text-lg font-semibold text-white mb-2">Demo Version</h3>
                        <p className="text-white/60 text-xs md:text-sm leading-relaxed mb-4">
                          Try the free demo version of this game. Purchase the full version to unlock all content and features.
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        {game.demoDownloads.windows && (
                          <button
                            onClick={() => handleDownload('windows', 'demo')}
                            disabled={downloading.platform === 'windows' && downloading.type === 'demo'}
                            className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 hover:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {downloading.platform === 'windows' && downloading.type === 'demo' ? (
                              <>Downloading...</>
                            ) : (
                              <>
                                <span className="material-icons text-xl">desktop_windows</span>
                                Windows Demo
                              </>
                            )}
                          </button>
                        )}
                        {game.demoDownloads.mac && (
                          <button
                            onClick={() => handleDownload('mac', 'demo')}
                            disabled={downloading.platform === 'mac' && downloading.type === 'demo'}
                            className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 hover:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {downloading.platform === 'mac' && downloading.type === 'demo' ? (
                              <>Downloading...</>
                            ) : (
                              <>
                                <span className="material-icons text-xl">laptop_mac</span>
                                Mac Demo
                              </>
                            )}
                          </button>
                        )}
                        {game.demoDownloads.android && (
                          <button
                            onClick={() => handleDownload('android', 'demo')}
                            disabled={downloading.platform === 'android' && downloading.type === 'demo'}
                            className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 hover:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {downloading.platform === 'android' && downloading.type === 'demo' ? (
                              <>Downloading...</>
                            ) : (
                              <>
                                <span className="material-icons text-xl">android</span>
                                Android Demo
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Full Game Download/Purchase Section */}
                  <div>
                    {isPurchased ? (
                      // User owns the game - show download buttons
                      game.fullDownloads ? (
                        <div>
                          <div className="mb-4">
                            <h3 className="text-base md:text-lg font-semibold text-white mb-2">Full Game</h3>
                            <p className="text-white/60 text-xs md:text-sm leading-relaxed mb-4">
                              Download the complete version of the game for your platform.
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            {game.fullDownloads.windows && (
                              <button
                                onClick={() => handleDownload('windows', 'full')}
                                disabled={downloading.platform === 'windows' && downloading.type === 'full'}
                                className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {downloading.platform === 'windows' && downloading.type === 'full' ? (
                                  <>Downloading...</>
                                ) : (
                                  <>
                                    <span className="material-icons text-xl">desktop_windows</span>
                                    Windows
                                  </>
                                )}
                              </button>
                            )}
                            {game.fullDownloads.mac && (
                              <button
                                onClick={() => handleDownload('mac', 'full')}
                                disabled={downloading.platform === 'mac' && downloading.type === 'full'}
                                className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {downloading.platform === 'mac' && downloading.type === 'full' ? (
                                  <>Downloading...</>
                                ) : (
                                  <>
                                    <span className="material-icons text-xl">laptop_mac</span>
                                    Mac
                                  </>
                                )}
                              </button>
                            )}
                            {game.fullDownloads.android && (
                              <button
                                onClick={() => handleDownload('android', 'full')}
                                disabled={downloading.platform === 'android' && downloading.type === 'full'}
                                className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {downloading.platform === 'android' && downloading.type === 'full' ? (
                                  <>Downloading...</>
                                ) : (
                                  <>
                                    <span className="material-icons text-xl">android</span>
                                    Android
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-2">
                          {game.itchLink ? (
                            <a
                              href={game.itchLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 w-full"
                            >
                              <img src="/images/itch_logo.png" alt="Itch.io" className="w-5 h-5 object-contain" draggable={false} onDragStart={(e) => e.preventDefault()} />
                              <span className="text-sm font-medium">Available on Itch</span>
                            </a>
                          ) : (
                            <div className="text-white/60 text-sm text-center py-2">No downloads available</div>
                          )}
                        </div>
                      )
                    ) : isFreeGame(game) ? (
                    // Free game - show download buttons (check if fullDownloads exists, even if values are null for R2)
                    game.fullDownloads ? (
                      <div>
                        <div className="mb-4">
                          <h3 className="text-base md:text-lg font-semibold text-white mb-2">Full Game</h3>
                          <p className="text-white/60 text-xs md:text-sm leading-relaxed mb-4">
                            This game is free to download and play.
                          </p>
                        </div>
                        <div className="flex flex-col gap-2">
                          {game.fullDownloads.windows && (
                            <button
                              onClick={() => {
                                if (game.fullDownloads.windows && !game.fullDownloads.windows.includes('example.com')) {
                                  window.open(game.fullDownloads.windows, '_blank');
                                } else {
                                  handleDownload('windows', 'full');
                                }
                              }}
                              disabled={downloading.platform === 'windows'}
                              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {downloading.platform === 'windows' ? (
                                <>Downloading...</>
                              ) : (
                                <>
                                  <span className="material-icons text-xl">desktop_windows</span>
                                  Windows
                                </>
                              )}
                            </button>
                          )}
                          {game.fullDownloads.mac && (
                            <button
                              onClick={() => {
                                if (game.fullDownloads.mac && !game.fullDownloads.mac.includes('example.com')) {
                                  window.open(game.fullDownloads.mac, '_blank');
                                } else {
                                  handleDownload('mac', 'full');
                                }
                              }}
                              disabled={downloading.platform === 'mac'}
                              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {downloading.platform === 'mac' ? (
                                <>Downloading...</>
                              ) : (
                                <>
                                  <span className="material-icons text-xl">laptop_mac</span>
                                  Mac
                                </>
                              )}
                            </button>
                          )}
                          {game.fullDownloads.android && (
                            <button
                              onClick={() => {
                                if (game.fullDownloads.android && !game.fullDownloads.android.includes('example.com')) {
                                  window.open(game.fullDownloads.android, '_blank');
                                } else {
                                  handleDownload('android', 'full');
                                }
                              }}
                              disabled={downloading.platform === 'android'}
                              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {downloading.platform === 'android' ? (
                                <>Downloading...</>
                              ) : (
                                <>
                                  <span className="material-icons text-xl">android</span>
                                  Android
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        {game.itchLink ? (
                          <a
                            href={game.itchLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 w-full"
                          >
                            <img src="/images/itch_logo.png" alt="Itch.io" className="w-5 h-5 object-contain" draggable={false} onDragStart={(e) => e.preventDefault()} />
                            <span className="text-sm font-medium">Available on Itch</span>
                          </a>
                        ) : (
                          <div className="text-white/60 text-sm text-center py-2">No downloads available</div>
                        )}
                      </div>
                    )
                  ) : (
                    <div>
                      <div className="mb-4">
                        <h3 className="text-base md:text-lg font-semibold text-white mb-2">Full Game</h3>
                        <p className="text-white/60 text-xs md:text-sm leading-relaxed mb-4">
                          {game.demoVersion
                            ? 'Purchase the full version to unlock all content, features, and the complete story.'
                            : 'Purchase this game to download and play the complete version.'}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          if (!isAuthenticated) {
                            navigate('/signin');
                            return;
                          }
                          if (usesUsdCheckout(game) && !hasPaymentMethod) {
                            setOpenConfirmAfterCard(true);
                            setShowAddCardModal(true);
                            return;
                          }
                          setShowConfirmModal(true);
                        }}
                        className="w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90"
                      >
                        Purchase Full Game
                      </button>
                    </div>
                  )}
                  </div>
                </div>
          </div>
        </div>
      </div>
    </main>

      {/* Purchase Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-5" onClick={() => setShowConfirmModal(false)}>
          <div 
            className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl overflow-hidden max-w-lg w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Game Image */}
            <div className="aspect-[16/9] w-full relative overflow-hidden">
              {(game.mainImage || game.featuredImage) ? (
                <img 
                  src={game.mainImage || game.featuredImage} 
                  alt={game.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-dark-border via-dark-card to-dark-surface flex items-center justify-center">
                  <div className="text-white/40">Game Image</div>
                </div>
              )}
            </div>

            <div className="p-4 md:p-8">
              <h2 className="text-xl md:text-3xl font-bold text-white mb-2">Confirm Purchase</h2>
              <p className="text-white/60 mb-4 md:mb-6 text-sm md:text-base">Review your purchase details below</p>
              
              <div className="mb-6 md:mb-8 space-y-4 md:space-y-5">
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Game Title</div>
                  <div className="text-white text-lg md:text-xl font-semibold">{game.title}</div>
                </div>
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Developer</div>
                  <div className="text-white/80 text-base md:text-lg">{game.developer}</div>
                </div>
                <div className="pt-5 border-t border-dark-border">
                  <div className="space-y-4">
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider mb-2">Total Cost</div>
                      <GamePrice game={game} size="lg" />
                    </div>
                    {usesUsdCheckout(game) && !hasPaymentMethod && (
                      <button
                        type="button"
                        onClick={() => {
                          setOpenConfirmAfterCard(true);
                          setShowAddCardModal(true);
                        }}
                        className="w-full py-2.5 px-4 rounded-xl font-semibold text-sm transition-all duration-200 bg-accent-blue/20 border border-accent-blue/40 text-accent-blue hover:bg-accent-blue/30 hover:border-accent-blue/60"
                      >
                        Add payment method
                      </button>
                    )}
                    {!usesUsdCheckout(game) && (
                      <p className="text-white/60 text-sm">Card checkout is not available for this game yet.</p>
                    )}
                  </div>
                </div>
              </div>

              {purchaseError && (
                <div className="mb-4 bg-red-500/20 border border-red-500/30 rounded-xl p-4">
                  <p className="text-red-400 text-sm">{purchaseError}</p>
                </div>
              )}

              <div className="flex gap-2 md:gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPurchaseError('');
                  }}
                  disabled={purchasing}
                  className="flex-1 py-2.5 md:py-3.5 px-4 md:px-6 rounded-xl font-medium md:font-semibold text-xs md:text-base transition-all duration-200 bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 hover:border-white/20 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePurchase}
                  disabled={purchasing || !usesUsdCheckout(game) || !hasPaymentMethod}
                  className="flex-1 py-2.5 md:py-3.5 px-4 md:px-6 rounded-xl font-medium md:font-semibold text-xs md:text-base transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 shadow-lg shadow-accent-blue/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {purchasing ? 'Processing...' : 'Confirm Purchase'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-5" onClick={() => setShowSuccessModal(false)}>
          <div 
            className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl overflow-hidden max-w-lg w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Icon */}
            <div className="p-8 text-center">
              <div className="mb-6 flex justify-center">
                <div className="w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-500/50 flex items-center justify-center">
                  <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-white mb-2">Purchase Successful!</h2>
              <p className="text-white/60 mb-6">
                {game.title} has been added to your library.
              </p>

              <div className="bg-dark-surface/50 border border-dark-border rounded-xl p-4 mb-6 flex items-center justify-between">
                <span className="text-white/60 text-sm">Amount Paid</span>
                <GamePrice game={game} size="md" hideDiscount />
              </div>

              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 shadow-lg shadow-accent-blue/20"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Lightbox Modal */}
      {showScreenshotModal && game?.screenshots && game.screenshots.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-5"
          onClick={closeScreenshotModal}
        >
          {/* Close Button */}
          <button
            onClick={closeScreenshotModal}
            className="absolute top-5 right-5 z-10 w-12 h-12 rounded-full bg-dark-surface/80 border border-dark-border hover:bg-dark-surface hover:border-white/20 transition-all duration-200 flex items-center justify-center group"
            aria-label="Close screenshot"
          >
            <svg className="w-6 h-6 text-white group-hover:text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Previous Button */}
          {game.screenshots.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateScreenshot(-1);
              }}
              className="absolute left-5 top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-dark-surface/80 border border-dark-border hover:bg-dark-surface hover:border-accent-blue/40 transition-all duration-200 flex items-center justify-center group"
              aria-label="Previous screenshot"
            >
              <svg className="w-6 h-6 text-white group-hover:text-accent-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Next Button */}
          {game.screenshots.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateScreenshot(1);
              }}
              className="absolute right-5 top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-dark-surface/80 border border-dark-border hover:bg-dark-surface hover:border-accent-blue/40 transition-all duration-200 flex items-center justify-center group"
              aria-label="Next screenshot"
            >
              <svg className="w-6 h-6 text-white group-hover:text-accent-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Screenshot Image */}
          <div 
            className="max-w-7xl w-full max-h-[90vh] flex items-center justify-center px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex items-center justify-center w-full h-full min-h-0">
              <img
                src={game.screenshots[currentScreenshotIndex]}
                alt={`${game.title} screenshot ${currentScreenshotIndex + 1}`}
                className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-2xl shadow-2xl"
                style={{ display: 'block', margin: '0 auto' }}
              />
              
              {/* Screenshot Counter */}
              {game.screenshots.length > 1 && (
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-dark-surface/80 border border-dark-border rounded-full px-4 py-2 text-white text-sm">
                  {currentScreenshotIndex + 1} / {game.screenshots.length}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AddCardModal
        isOpen={showAddCardModal}
        onClose={() => {
          setShowAddCardModal(false);
          setOpenConfirmAfterCard(false);
        }}
        onSuccess={async () => {
          await fetchPaymentMethods();
          if (openConfirmAfterCard) {
            setShowConfirmModal(true);
            setOpenConfirmAfterCard(false);
          }
        }}
        title="Add a card to purchase"
        description={
          game
            ? `Save a card to buy ${game.title}. It stays on your account for future purchases.`
            : 'Save a card to your account for quick checkout.'
        }
      />

    </>
  );
}

export default GameDetail;

